/**
 * Where the Character tab gets each character from.
 *
 * The child has a dedicated artboard authored for editing
 * (`sb_character_editor.riv` → "Editor", 733×1238 — a full standing figure).
 * Authoring one of those per character is the work this file exists to avoid:
 * **Dad and Mom are edited on the shared animation scene artboard instead, framed
 * to their own head** (Tamara, 2026-07-31 — "it is inconvenient for me to prepare
 * each character the same way I did for child"). Nothing in Rive has to change for
 * a new character beyond being in the scene with their own ViewModel + pose prefix.
 *
 * Everything that differs between the two ways of mounting a character lives in
 * {@link EditorSource}, so the screen itself has one code path — including the
 * head framing, so all three open at the same scale.
 */
import {
  RIV_SRC,
  pickHeadArtboard,
  EYE_PUPIL_ASSET,
  FRECKLES_ASSET,
  GLASSES_ASSET,
  HAIR_LAYER_ASSETS,
} from './contract';
import {
  CHARACTERS,
  CHARACTER_FRAMES,
  CHARACTER_POSE_PREFIX,
  SCENE_CHARACTER_BINDINGS,
  SCENE_CHARACTER_IMAGE_SLOTS,
  SCENE_REF_W,
  SCENE_REF_H,
  SCENE_RIV_SRC,
  sceneNumber,
  type CharacterId,
  type CharacterImageSlots,
} from './animationContract';
import type { ViewRect } from './riveView';

/** The child's editing artboard — a full standing figure. */
const EDITOR_REF_W = 733;
const EDITOR_REF_H = 1238;

/**
 * The child's head on that artboard, in artboard pixels.
 *
 * The head+hair is ~300 units tall around a centre at y≈300, and this rect is
 * measured off it — the child opens on their face like Dad and Mom do out of the
 * scene ({@link CHARACTER_FRAMES}, ~730×720 of 2400×1400), a little WIDER than
 * theirs so more of the shoulders and chest is in view (Tamara, 2026-08-03 — a
 * closer 165% was tried first and backed out, then two "a bit wider" steps off the
 * head-matching ~147%; this reads ~115%). The zoom a rect reads out as depends on the preview's
 * ASPECT, not its size (both `zoom` and the Contain fit scale with the canvas), so
 * that number holds at any window size and only drifts if the panel is resized to a
 * different shape. Sitting a little above the head's centre is deliberate — as with
 * theirs, a head floating dead centre reads worse.
 */
const EDITOR_CHILD_FRAME: ViewRect = { x: 14, y: 2, w: 705, h: 705 };

export interface EditorSource {
  id: CharacterId;
  label: string;
  /** The .riv the stage mounts. */
  src: string;
  /** Pick this character's artboard out of the file's artboard names. */
  pickArtboard: (names: string[]) => string | undefined;
  /** Size of that artboard — the coordinate space {@link frame} is written in. */
  refW: number;
  refH: number;
  /**
   * Property on the root ViewModel holding this character's nested VM.
   * `undefined` ⇒ the artboard's own ViewModel is the character's (the child's
   * `ViewModel1`).
   */
  rootProp?: string;
  /**
   * Prefix on this character's pose timelines / CTRL timelines in the file
   * (`''` for the child, `D_` dad, `M_` mom).
   */
  posePrefix: string;
  /** Image slots this character owns. Empty fields fall back to detection. */
  imageSlots: CharacterImageSlots;
  /**
   * Detect the two hair slots by load order instead of trusting
   * {@link imageSlots}. True only for the head-editor file, whose slot names
   * change with every export and can't be told apart by name.
   */
  detectHairSlots: boolean;
  /**
   * Framing preset in artboard pixels — `undefined` shows the artboard as-is.
   * Set for every character so they all open at the same head scale: Dad and Mom
   * are zoomed out of the 2400×1400 stage, the child out of their own standing
   * figure.
   */
  frame?: ViewRect;
  /** Scene artboards have no ViewModel linked by default; bind one by hand. */
  bindRootViewModel: boolean;
}

/** The child's head-only artboard: the original, unchanged mount. */
function childSource(): EditorSource {
  return {
    id: 'child',
    label: 'Child',
    src: RIV_SRC,
    pickArtboard: pickHeadArtboard,
    refW: EDITOR_REF_W,
    refH: EDITOR_REF_H,
    posePrefix: CHARACTER_POSE_PREFIX.child,
    imageSlots: {
      hairFront: HAIR_LAYER_ASSETS.front,
      hairBack: HAIR_LAYER_ASSETS.back,
      pupil: EYE_PUPIL_ASSET,
      freckles: FRECKLES_ASSET,
      glasses: GLASSES_ASSET,
    },
    detectHairSlots: true,
    frame: EDITOR_CHILD_FRAME,
    bindRootViewModel: false,
  };
}

/** Dad / Mom: framed out of the animation scene's first scene artboard. */
function sceneSource(id: CharacterId): EditorSource {
  return {
    id,
    label: CHARACTERS.find((c) => c.id === id)?.label ?? id,
    src: SCENE_RIV_SRC,
    // The LOWEST-numbered scene artboard, never the master: a master artboard
    // only holds the scene chain (so no pose timelines and no linked ViewModel),
    // and scene 1 is the standing pose the characters are authored in.
    pickArtboard: (names) => {
      const scenes = names
        .filter((n) => sceneNumber(n) !== undefined)
        .sort((a, b) => (sceneNumber(a) ?? 0) - (sceneNumber(b) ?? 0));
      return scenes[0];
    },
    refW: SCENE_REF_W,
    refH: SCENE_REF_H,
    rootProp: SCENE_CHARACTER_BINDINGS[id].rootProp,
    posePrefix: CHARACTER_POSE_PREFIX[id],
    imageSlots: SCENE_CHARACTER_IMAGE_SLOTS[id],
    detectHairSlots: false,
    frame: CHARACTER_FRAMES[id],
    bindRootViewModel: true,
  };
}

export function editorSourceFor(id: CharacterId): EditorSource {
  return id === 'child' ? childSource() : sceneSource(id);
}

/** Characters the Character tab offers, in chooser order. */
export const EDITOR_CHARACTERS: CharacterId[] = ['child', 'dad', 'mom'];
